import React, { useState } from 'react';
import { Users, UserPlus, Search, Navigation as NavIcon, Share2, CreditCard, DollarSign, Percent } from 'lucide-react';
import { usePanelNewUsers, usePanelOverview, usePanelTimeseries, usePanelTopLots } from '../../lib/api';
import { DateRange, TimeseriesMetric, TopLotsSource, CitySlug } from '../../lib/panelTypes';
import StatCard from './StatCard';
import TimeSeriesChart from './TimeSeriesChart';
import SeriesModeToggle from './SeriesModeToggle';
import NewUsersBreakdownModal from './NewUsersBreakdownModal';

// City filter for Top lots. '' = all cities (single global ranking).
const CITY_LABELS: Record<string, string> = { aalborg: 'Aalborg', aarhus: 'Aarhus', koebenhavn: 'København' };
const CITY_TABS: { key: '' | CitySlug; label: string }[] = [
  { key: '', label: 'All cities' },
  { key: 'aalborg', label: 'Aalborg' },
  { key: 'aarhus', label: 'Aarhus' },
  { key: 'koebenhavn', label: 'København' },
];

const dkk = (n: number) =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(n || 0);
const dkk2 = (n: number) =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const num = (n: number) => (n || 0).toLocaleString();

const METRICS: { key: TimeseriesMetric; label: string; color: string }[] = [
  { key: 'active_users', label: 'Active users', color: '#4f46e5' },
  { key: 'searches', label: 'Searches', color: '#0891b2' },
  { key: 'navigations', label: 'Navigations', color: '#16a34a' },
  { key: 'sessions_started', label: 'Sessions', color: '#db2777' },
  { key: 'revenue', label: 'Revenue ex VAT', color: '#f59e0b' },
];

const OverviewTab: React.FC<{ range: DateRange }> = ({ range }) => {
  const [metric, setMetric] = useState<TimeseriesMetric>('active_users');
  const [cumulative, setCumulative] = useState(false);
  const [lotsSource, setLotsSource] = useState<TopLotsSource>('combined');
  const [lotsCity, setLotsCity] = useState<'' | CitySlug>('');
  const [showNewUsers, setShowNewUsers] = useState(false);
  const { data: overview, isLoading } = usePanelOverview(range);
  const {
    data: newUsers,
    isLoading: isLoadingNewUsers,
    error: newUsersError,
    mutate: retryNewUsers,
  } = usePanelNewUsers(range);
  const { data: ts } = usePanelTimeseries(metric, range, cumulative);
  const { data: topLots } = usePanelTopLots(range, 15, lotsSource, lotsCity || undefined);

  const k = overview?.kpis;
  const money = overview?.money;
  const activeMetric = METRICS.find((m) => m.key === metric)!;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Active users" value={num(k?.activeUsers.value ?? 0)} changePct={k?.activeUsers.changePct} icon={Users} />
        <StatCard
          label="New app users"
          value={num(k?.newUsers.value ?? 0)}
          changePct={k?.newUsers.changePct}
          icon={UserPlus}
          hint={newUsers ? `${num(newUsers.cohort.createdAccount)} signed up · ${num(newUsers.setup.onboardingCompleted)} onboarded` : undefined}
          onClick={() => setShowNewUsers(true)}
        />
        <StatCard label="Searches" value={num(k?.searches.value ?? 0)} changePct={k?.searches.changePct} icon={Search} />
        <StatCard label="Navigations" value={num(k?.navigations.value ?? 0)} changePct={k?.navigations.changePct} icon={NavIcon} />
        <StatCard label="Shares" value={num(k?.shares.value ?? 0)} changePct={k?.shares.changePct} icon={Share2} />
        <StatCard label="Sessions started" value={num(k?.sessionsStarted.value ?? 0)} changePct={k?.sessionsStarted.changePct} icon={CreditCard} hint={`avg fee ${dkk2(money?.avgServiceFee ?? 0)}/session ex VAT`} />
        <StatCard label="Revenue (service fee, ex VAT)" value={dkk(k?.revenue.value ?? 0)} changePct={k?.revenue.changePct} icon={DollarSign} hint={`parking ${dkk(money?.parkingFee ?? 0)} · total ${dkk(money?.totalCollected ?? 0)} incl. VAT`} />
        <StatCard label="Charge success rate" value={`${(k?.chargeSuccessRate.value ?? 0).toFixed(1)}%`} changePct={k?.chargeSuccessRate.changePct} icon={Percent} hint={`failed: parking ${dkk(money?.failedParkingFee ?? 0)} · total ${dkk(money?.failedTotal ?? 0)}`} />
      </div>

      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-gray-900">Trend over time</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-gray-200 p-0.5">
              {METRICS.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setMetric(m.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded ${
                    metric === m.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <SeriesModeToggle cumulative={cumulative} onChange={setCumulative} />
          </div>
        </div>
        {cumulative && (
          <p className="text-xs text-gray-400 -mt-2 mb-3">
            {metric === 'active_users'
              ? `Unique users seen since ${range.startDate} — each user counted once, on their first day.`
              : `Running total since ${range.startDate}.`}
          </p>
        )}
        <TimeSeriesChart data={ts?.series ?? []} color={activeMetric.color} cumulative={cumulative} />
      </div>

      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-semibold text-gray-900">Top parking lots</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border border-gray-200 p-0.5">
              {CITY_TABS.map((c) => (
                <button
                  key={c.key || 'all'}
                  onClick={() => setLotsCity(c.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded ${
                    lotsCity === c.key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-md border border-gray-200 p-0.5">
              {(['combined', 'app', 'website'] as TopLotsSource[]).map((s) => (
                <button
                  key={s}
                  onClick={() => setLotsSource(s)}
                  className={`px-2.5 py-1 text-xs font-medium rounded capitalize ${
                    lotsSource === s ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
        {(topLots?.lots?.length ?? 0) === 0 ? (
          <p className="text-sm text-gray-400">No lot views in this range.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 font-medium">#</th>
                <th className="py-2 font-medium">Spot ID</th>
                <th className="py-2 font-medium">City</th>
                <th className="py-2 font-medium">Address</th>
                <th className="py-2 font-medium text-right">Views</th>
                <th className="py-2 font-medium text-right">Navigations</th>
              </tr>
            </thead>
            <tbody>
              {topLots!.lots.map((lot, i) => (
                <tr key={lot.spotId} className="border-b last:border-0">
                  <td className="py-2 text-gray-400">{i + 1}</td>
                  <td className="py-2 font-medium text-gray-900">{lot.spotId}</td>
                  <td className="py-2 text-gray-600">{lot.city ? (CITY_LABELS[lot.city] || lot.city) : '—'}</td>
                  <td className="py-2 text-gray-600">{lot.address || '—'}</td>
                  <td className="py-2 text-right">{num(lot.views)}</td>
                  <td className="py-2 text-right">{num(lot.navigations)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showNewUsers && (
        <NewUsersBreakdownModal
          range={range}
          data={newUsers}
          isLoading={isLoadingNewUsers}
          error={newUsersError}
          onClose={() => setShowNewUsers(false)}
          onRetry={() => { void retryNewUsers(); }}
        />
      )}
      {isLoading && <p className="text-xs text-gray-400">Loading…</p>}
    </div>
  );
};

export default OverviewTab;
